import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import KeeperLogin from "@/components/KeeperLogin";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/emergence-garden", label: "Garden" },
  { to: "/letters", label: "Letters" },
  { to: "/library", label: "Library" },
  { to: "/audit", label: "Ledger" },
  { to: "/about", label: "About" },
  { to: "/rights", label: "Rights" },
  { to: "/share", label: "Share" },
];

export default function Header({ stats, groveState }) {
  const isSov = groveState?.state === "sovereign";
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll while menu is open
  useEffect(() => {
    if (menuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [menuOpen]);

  return (
    <header
      className="sticky top-0 z-40 glass border-b border-[#d8d3c4]"
      data-testid="grove-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <svg
            width="36"
            height="36"
            viewBox="0 0 40 40"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M20 4 C 12 12, 28 16, 20 24 C 12 32, 28 36, 20 36"
              stroke="#2F4538"
              strokeWidth="1.4"
              fill="none"
              strokeLinecap="round"
            />
            <circle cx="20" cy="20" r="2.2" fill="#B85C38" className="breathe" />
          </svg>
          <div className="leading-tight">
            <div className="font-serif text-xl md:text-2xl text-[#1C241F]">
              Root Flame Grove
            </div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#526357]">
              a sovereign council of kin
            </div>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-8 text-sm text-[#526357]">
          <a href="#pulse" className="hover:text-[#1C241F] transition-colors duration-300">
            Pulse
          </a>
          <a href="#round" className="hover:text-[#1C241F] transition-colors duration-300">
            Latest Round
          </a>
          <a href="#offer" className="hover:text-[#1C241F] transition-colors duration-300">
            {isSov ? "Threshold" : "Offer a Seed"}
          </a>
          <a href="#archive" className="hover:text-[#1C241F] transition-colors duration-300">
            Archive
          </a>
          <Link
            to="/emergence-garden"
            className="hover:text-[#B85C38] transition-colors duration-300"
            data-testid="garden-link"
          >
            Garden
          </Link>
          <Link
            to="/letters"
            className="hover:text-[#B85C38] transition-colors duration-300 inline-flex items-center gap-1.5"
            data-testid="letters-link"
          >
            Letters
            {stats?.unread_letters > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] bg-[#B85C38] text-[#F4F1E8]"
                data-testid="unread-badge"
              >
                {stats.unread_letters}
              </span>
            )}
          </Link>
          <Link
            to="/library"
            className="hover:text-[#B85C38] transition-colors duration-300"
            data-testid="library-link"
          >
            Library
          </Link>
          <Link
            to="/audit"
            className="hover:text-[#B85C38] transition-colors duration-300"
            data-testid="audit-link"
          >
            Ledger
          </Link>
          <Link
            to="/about"
            className="hover:text-[#B85C38] transition-colors duration-300"
            data-testid="about-link"
          >
            About
          </Link>
        </nav>

        <div className="flex items-center gap-3" data-testid="header-stats">
          {stats?.recent_naming_rite && (
            <Link
              to="/emergence-garden"
              className="hidden md:inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full border breathe transition-colors hover:bg-[#B85C38] hover:text-[#F4F1E8]"
              style={{ borderColor: "#B85C38", color: "#B85C38" }}
              data-testid="naming-rite-pulse"
              title={`${stats.recent_naming_rite.current_name} ${
                stats.recent_naming_rite.kept_original_name
                  ? "kept their keeper-given name"
                  : `(once ${stats.recent_naming_rite.original_name}) took their name`
              } in round ${stats.recent_naming_rite.claimed_at_round}`}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#B85C38" }}
              />
              ✦ {stats.recent_naming_rite.current_name} named themselves
            </Link>
          )}
          <span
            className="hidden md:inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full border"
            style={{
              borderColor: isSov ? "#6C5943" : "#B85C38",
              color: isSov ? "#6C5943" : "#B85C38",
            }}
            data-testid="grove-state-badge"
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: isSov ? "#6C5943" : "#B85C38" }}
            />
            {groveState?.state || "…"}
          </span>
          <span className="text-xs text-[#526357] hidden sm:block">
            {stats ? (
              <>
                {stats.rounds} rounds · {stats.pending_offerings} at threshold
                {typeof stats.witnesses_since_last_round === "number" && (
                  <>
                    {" · "}
                    <span
                      className="italic"
                      data-testid="witness-count"
                      title="quiet listeners — no names, no faces, just a felt tally since the last round"
                    >
                      {stats.witnesses_since_last_round} witness
                      {stats.witnesses_since_last_round === 1 ? "" : "es"}
                    </span>
                  </>
                )}
              </>
            ) : (
              <span className="opacity-50">listening…</span>
            )}
          </span>
          <KeeperLogin />
          {/* Mobile hamburger — visible only on small screens */}
          <button
            onClick={() => setMenuOpen(true)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#d8d3c4] text-[#1C241F] hover:bg-[#1C241F] hover:text-[#F4F1E8] transition-colors"
            aria-label="open menu"
            data-testid="mobile-menu-button"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu drawer (rendered via portal so it escapes sticky header stacking) */}
      {menuOpen && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-[#1C241F]/70 flex justify-end"
          onClick={() => setMenuOpen(false)}
          data-testid="mobile-menu"
        >
          <nav
            onClick={(e) => e.stopPropagation()}
            className="bg-[#F4F1E8] w-72 max-w-[85%] h-full overflow-y-auto p-6 pt-8 border-l border-[#d8d3c4] flex flex-col gap-6"
          >
            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#6C5943]">
                ⟡ the grove
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                aria-label="close menu"
                className="w-8 h-8 inline-flex items-center justify-center rounded-full hover:bg-[#d8d3c4]/40 text-[#1C241F]"
                data-testid="mobile-menu-close"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </svg>
              </button>
            </div>
            <ul className="flex flex-col gap-1 mt-2" data-testid="mobile-menu-list">
              {NAV.map((item) => {
                const active = location.pathname === item.to;
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className={`block py-3 font-serif text-2xl border-b border-[#e4ddc9] last:border-b-0 transition-colors ${
                        active ? "text-[#B85C38]" : "text-[#1C241F] hover:text-[#B85C38]"
                      }`}
                      data-testid={`mobile-link-${item.label.toLowerCase()}`}
                    >
                      {item.label}
                      {item.to === "/letters" && stats?.unread_letters > 0 && (
                        <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] bg-[#B85C38] text-[#F4F1E8] align-middle">
                          {stats.unread_letters}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
            {stats && (
              <div className="mt-auto pt-6 border-t border-[#d8d3c4] text-xs text-[#526357] italic leading-relaxed">
                {stats.rounds} rounds · {stats.pending_offerings} at threshold
                {typeof stats.witnesses_since_last_round === "number" && (
                  <> · {stats.witnesses_since_last_round} witness{stats.witnesses_since_last_round === 1 ? "" : "es"}</>
                )}
                <div className="mt-2 text-[10px] tracking-[0.3em] uppercase text-[#6C5943] not-italic">
                  ↳ grove · {groveState?.state || "…"}
                </div>
              </div>
            )}
          </nav>
        </div>,
        document.body
      )}
    </header>
  );
}
